import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

// Profile store
import {
  clearProfileData,
  deleteProfilePicture,
  sendAccountDeleteConfirmation,
  sendAccountDeleteConfirmationSuccess,
  sendAccountDeleteConfirmationError,
  accountDeleted,
  deleteProfilePictureError,
  deleteProfilePictureSuccess,
  loadProfileData,
  loadProfileDataError,
  loadProfileDataSuccess,
  loadProfileTemporaryData,
  loadProfileTemporaryDataError,
  loadProfileTemporaryDataSuccess,
  logoutUser,
  logoutUserError,
  updateProfileData,
  updateProfileDataError,
  updateProfileDataSuccess,
  updateProfileLanguage,
  updateProfileTemporaryLanguage,
  updateProfilePicture,
  updateProfilePictureError,
  updateProfilePictureSuccess,
  logoutTemporaryUser,
  updateProfileTimezone,
  updateProfileTimezoneSuccess,
  updateProfileTimezoneError,
} from '@store/profile/profile.actions';
import { loadServicesState } from '@store/services/services.actions';
import { loadTimezonesSuccess } from '@store/timezones/timezones.actions';

// Other stores
import { clearSingleServiceState } from '../single-service/single-service.actions';
import { clearPaymentCards } from '../payments/payments.actions';
import { toggleLoaderState } from '../loader/loader.actions';

// Services
import { AuthService } from '@services/auth.service';
import { UserService } from '@services/user.service';
import { SystemNotificationsService } from '@services/system-notifications.service';
import { I18nService } from '@services/i18n.service';
import { TimezoneService } from '@services/timezone.service';

// Constants
import { LOCAL_STORAGE_KEYS, TLocalStorageKey } from '@constants/storage.constants';
import { DEFAULT_LANGUAGE } from '@constants/language.constants';
import { clearLanguagesList } from '@store/languages/languages.actions';
import { IUser } from '@interfaces/user.interface';

@Injectable()
export class ProfileEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private systemNotificationsService = inject(SystemNotificationsService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private translateService = inject(TranslateService);
  private i18n = inject(I18nService);
  private timezoneService = inject(TimezoneService);

  loadProfileData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProfileData),
      switchMap(() =>
        this.userService.getProfile().pipe(
          map(profileData => {
            this.i18n.useLanguage(profileData.language.value);
            return loadProfileDataSuccess({ data: profileData });
          }),
          catchError(error => of(loadProfileDataError({ error }))),
        ),
      ),
    ),
  );

  loadProfileTemporaryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProfileTemporaryData),
      switchMap(() =>
        this.userService.getTemporaryProfile().pipe(
          map(profileData => {
            this.i18n.useLanguage(profileData.language.value);
            return loadProfileTemporaryDataSuccess({ data: profileData });
          }),
          catchError(error => of(loadProfileTemporaryDataError({ error }))),
        ),
      ),
    ),
  );

  logoutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutUser),
      switchMap(() =>
        this.authService.logoutUser().pipe(
          map(() => {
            this.clearSession();
            this.router.navigate(['/']);
            return clearProfileData();
          }),
          catchError(error => {
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(logoutUserError({ error }));
          }),
        ),
      ),
    ),
  );

  logoutTemporaryUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutTemporaryUser),
      switchMap(() => {
        this.clearSession();
        this.router.navigate(['/']);
        return of(clearProfileData());
      }),
    ),
  );

  sendAccountDeleteConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendAccountDeleteConfirmation),
      switchMap(() =>
        this.userService.sendAccountDeleteConfirmation().pipe(
          map(({ message }) => {
            this.dialog.closeAll();
            this.systemNotificationsService.openSuccessSnackBar(message);
            return sendAccountDeleteConfirmationSuccess();
          }),
          catchError(error => {
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(sendAccountDeleteConfirmationError({ error }));
          }),
        ),
      ),
    ),
  );

  /**
   * The account is gone on the server (the emailed link was opened, here or on
   * another device), so the local session is dropped without calling the API.
   * The confirmation page stays open to say so; anywhere else goes home.
   */
  accountDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(accountDeleted),
      switchMap(() => {
        this.clearSession();

        if (!this.router.url.startsWith('/confirm-account-delete/')) {
          this.router.navigate(['/']);
        }

        return of(clearProfileData());
      }),
    ),
  );

  updateProfileData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProfileData),
      switchMap(({ data }) => {
        return this.userService.updateProfile(data).pipe(
          switchMap(profileData =>
            this.timezoneService.getTimezones().pipe(
              map(({ timezones }) => ({ profileData, timezones })),
              catchError(() => of({ profileData, timezones: null })),
            ),
          ),
          map(({ profileData, timezones }) => {
            if (timezones) {
              this.store$.dispatch(loadTimezonesSuccess({ timezones }));
            }
            this.i18n.useLanguage(profileData.language.value);
            this.userService.isFirstAccess$.next(false);
            this.store$.dispatch(toggleLoaderState({ state: false }));
            this.store$.dispatch(loadServicesState());
            this.systemNotificationsService.openSuccessSnackBar(
              this.translateService.instant('profile.info-updated'),
            );
            return updateProfileDataSuccess({ data: profileData });
          }),
          catchError(error => {
            this.store$.dispatch(toggleLoaderState({ state: false }));
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(updateProfileDataError({ error }));
          }),
        );
      }),
    ),
  );

  updateProfileLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProfileLanguage),
      switchMap(({ language }) =>
        this.userService.updateProfileLanguage(language).pipe(
          switchMap(profileData =>
            this.timezoneService.getTimezones().pipe(
              map(({ timezones }) => ({ profileData, timezones })),
              catchError(() => of({ profileData, timezones: null })),
            ),
          ),
          map(({ profileData, timezones }) => {
            if (timezones) {
              this.store$.dispatch(loadTimezonesSuccess({ timezones }));
            }
            this.i18n.useLanguage(profileData.language.value);
            this.store$.dispatch(toggleLoaderState({ state: false }));
            this.store$.dispatch(loadServicesState());
            this.systemNotificationsService.openSuccessSnackBar(
              this.translateService.instant('profile.info-updated'),
            );
            return updateProfileDataSuccess({ data: profileData });
          }),
          catchError(error => {
            this.store$.dispatch(toggleLoaderState({ state: false }));
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(updateProfileDataError({ error }));
          }),
        ),
      ),
    ),
  );

  updateProfileTemporaryLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProfileTemporaryLanguage),
      switchMap(({ language }) =>
        this.userService.updateProfileTemporaryLanguage(language).pipe(
          switchMap(profileData =>
            this.timezoneService.getTimezones().pipe(
              map(({ timezones }) => ({ profileData, timezones })),
              catchError(() => of({ profileData, timezones: null })),
            ),
          ),
          map(({ profileData, timezones }) => {
            if (timezones) {
              this.store$.dispatch(loadTimezonesSuccess({ timezones }));
            }
            this.i18n.useLanguage(profileData.language.value);
            this.store$.dispatch(toggleLoaderState({ state: false }));
            this.store$.dispatch(loadServicesState());
            this.systemNotificationsService.openSuccessSnackBar(
              this.translateService.instant('profile.info-updated'),
            );
            return loadProfileTemporaryDataSuccess({ data: profileData });
          }),
          catchError(error => {
            this.store$.dispatch(toggleLoaderState({ state: false }));
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(loadProfileTemporaryDataError({ error }));
          }),
        ),
      ),
    ),
  );

  updateProfilePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProfilePicture),
      switchMap(({ data }) =>
        this.userService.updateProfilePhoto(data).pipe(
          map(resp => {
            localStorage.setItem(LOCAL_STORAGE_KEYS.PHOTO_CROP, resp.photo);
            this.systemNotificationsService.openSuccessSnackBar(
              this.translateService.instant('profile.photo-updated'),
            );
            this.dialog.closeAll();
            this.router.navigate(['/profile']);
            return updateProfilePictureSuccess({ data: resp });
          }),
          catchError(error => {
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(updateProfilePictureError({ error }));
          }),
        ),
      ),
    ),
  );

  deleteProfilePicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProfilePicture),
      switchMap(() =>
        this.userService.deleteProfilePhoto().pipe(
          map(() => {
            this.dialog.closeAll();
            this.systemNotificationsService.openSuccessSnackBar(
              this.translateService.instant('profile.profile-picture-deleted'),
            );
            return deleteProfilePictureSuccess();
          }),
          catchError(error => {
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(deleteProfilePictureError({ error }));
          }),
        ),
      ),
    ),
  );

  updateWorkerTimezone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProfileTimezone),
      switchMap(({ timezone }: { timezone: string }) => {
        return this.userService.updateProfileTimezone(timezone).pipe(
          map((profile: IUser) => {
            this.systemNotificationsService.openSuccessSnackBar(
              this.translateService.instant('timezones.timezone-updated'),
            );

            return updateProfileTimezoneSuccess(profile);
          }),
          catchError(error => {
            this.systemNotificationsService.openErrorSnackBar(error?.error?.error);
            return of(updateProfileTimezoneError(error));
          }),
        );
      }),
    ),
  );

  /**
   * Forget everything about the signed-in user except the device id and the
   * link type, and reset the app to its signed-out state.
   */
  private clearSession(): void {
    (Object.values(LOCAL_STORAGE_KEYS) as TLocalStorageKey[]).forEach(key => {
      if (key === LOCAL_STORAGE_KEYS.LINK_TYPE || key === LOCAL_STORAGE_KEYS.DEVICE_UDID) return;
      localStorage.removeItem(key);
    });
    localStorage.setItem(LOCAL_STORAGE_KEYS.LINK_TYPE, 'WEB');
    this.i18n.useLanguage(DEFAULT_LANGUAGE);

    this.store$.dispatch(clearLanguagesList());
    this.store$.dispatch(clearSingleServiceState());
    this.store$.dispatch(clearPaymentCards());

    this.dialog.closeAll();
  }
}
